/** GPU Median — fragment-shader port of `lib/filters/median.ts`.
 *
 *  Per output pixel, collect the (2r+1) × (2r+1) source window's
 *  integerized per-channel values, then find each channel's
 *  median via 8-bit binary search:
 *
 *    For bit b = 7..0:
 *      candidate mid = lo | (1 << b)
 *      count = number of samples strictly less than mid
 *      If count <= targetRank: bit b of median is 1, lo = mid
 *      Else:                    bit b of median is 0, lo stays
 *    After 8 iterations, lo is the median.
 *
 *  This is the standard "histogram-binary-search" selection trick.
 *  Total per-channel work: 8 outer iters × n samples = 8n compares.
 *  At r=3 (n=49) that's 392 ops per channel = 1176 per pixel
 *  (vs ~3700 for selection sort), AND the inner loop only does
 *  sequential `samples[i]` reads — no dynamic-index lookups, so
 *  compilers don't spill to local memory and the WebGL driver
 *  doesn't trip its per-draw-call timeout on big images.
 *
 *  CPU uses Huang's sliding-histogram trick — O(W·H·r) overall by
 *  reusing row state. That's a sequential algorithm; the GPU can't
 *  replicate it efficiently because every fragment runs in
 *  isolation. The binary-search approach trades CPU's row-state
 *  reuse for embarrassing parallelism across fragments.
 *
 *  Sort happens in 0..255 integer space (samples * 255.0,
 *  integerized via floor(+0.5)) to mirror the CPU's Uint8
 *  histogram bins exactly. Output gets renormalized to 0..1 for
 *  the framebuffer.
 *
 *  Radius 0 short-circuits to identity. Programmatic callers with
 *  radius > 3 fall back to CPU. Radially symmetric — no Y-flip
 *  needed (the iteration order doesn't affect the median). */

import type { MedianValue } from '../../filters/median';
import { applyIdentityCopy } from '../identity-copy';
import { applyShader } from '../runtime';

const MAX_RADIUS = 3;
// 7x7 = 49 — the maximum window size at MAX_RADIUS. Smaller radii
// leave the tail of the local arrays unused, gated by `n` checks
// inside the binary-search inner loop.
const MAX_N = (2 * MAX_RADIUS + 1) * (2 * MAX_RADIUS + 1);

const MEDIAN_FRAG = `#version 300 es
precision highp float;
precision highp int;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform int u_radius;
out vec4 outColor;

// Find the median of samples[0..n) via 8-bit binary search.
// Inner loop only does sequential samples[i] access with i a
// predictable loop induction variable — no dynamic indexing, so
// the compiler can keep the array in registers and the per-draw-
// call runtime stays well under the driver TDR threshold.
int findMedianByBits(in int samples[${MAX_N}], int n, int targetRank) {
  int lo = 0;
  for (int b = 0; b < 8; b++) {
    int mid = lo | (1 << (7 - b));
    int count = 0;
    for (int i = 0; i < ${MAX_N}; i++) {
      if (i >= n) break;
      if (samples[i] < mid) count++;
    }
    if (count <= targetRank) lo = mid;
  }
  return lo;
}

void main() {
  vec2 px = v_uv * u_dims - 0.5;
  int kSize = 2 * u_radius + 1;
  int n = kSize * kSize;
  // For odd n (always true since 2r+1 is odd), the median is at
  // sorted-index n / 2 (0-indexed). Matches CPU's halfWin scan
  // endpoint: smallest value v where count(<= v) > n/2.
  int targetRank = n / 2;

  int rSamples[${MAX_N}];
  int gSamples[${MAX_N}];
  int bSamples[${MAX_N}];
  int idx = 0;
  // Constant-bound 7x7 loop; runtime cutoff skips taps outside the
  // current radius. dy/dx iteration order doesn't affect the median.
  for (int dy = -${MAX_RADIUS}; dy <= ${MAX_RADIUS}; dy++) {
    if (dy < -u_radius || dy > u_radius) continue;
    for (int dx = -${MAX_RADIUS}; dx <= ${MAX_RADIUS}; dx++) {
      if (dx < -u_radius || dx > u_radius) continue;
      vec2 sxy = clamp(px + vec2(float(dx), float(dy)), vec2(0.0), u_dims - 1.0);
      vec2 uv = (sxy + 0.5) / u_dims;
      vec3 v = texture(u_source, uv).rgb;
      // Integerize to match CPU's Uint8 histogram. Without this, FP
      // sample noise could shift the sort order for samples whose
      // float values are within ~1/255 of each other.
      rSamples[idx] = int(floor(v.r * 255.0 + 0.5));
      gSamples[idx] = int(floor(v.g * 255.0 + 0.5));
      bSamples[idx] = int(floor(v.b * 255.0 + 0.5));
      idx++;
    }
  }

  int rMed = findMedianByBits(rSamples, n, targetRank);
  int gMed = findMedianByBits(gSamples, n, targetRank);
  int bMed = findMedianByBits(bSamples, n, targetRank);

  // Alpha passes through unchanged — matches the CPU path, which
  // copies src alpha directly and never runs it through the median.
  vec4 src = texture(u_source, v_uv);
  vec3 median = vec3(float(rMed), float(gMed), float(bMed)) / 255.0;
  outColor = vec4(median, src.a);
}
`;

export function applyMedianOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: MedianValue,
): boolean {
  const radius = Math.max(0, Math.min(MAX_RADIUS, Math.floor(value.radius)));
  if (radius === 0) return applyIdentityCopy(source, target);
  // Defensive — UI caps at 3 but the clamp above handles that. The
  // explicit check below catches the case where a future UI change
  // raises the cap past what the shader's constant-bound loop can
  // express.
  if (radius > MAX_RADIUS) return false;
  return applyShader({
    source,
    target,
    fragShaderSrc: MEDIAN_FRAG,
    uniforms: {
      u_dims: { type: 'vec2', value: [source.width, source.height] },
      u_radius: { type: 'int', value: radius },
    },
  });
}
